// src/pages/Requests/EmployeeAllRequestsPage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api'; // axios instance
import { buildPublicFileUrl } from '../../config/env';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Stack,
  TablePagination,
  Chip,
  Link,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';

// Icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { cancelExitEntryRequest, updateRequestStatus } from '../../services/exitEntryApi';
import { EmptyState, LoadingState, PageLayout, StatusBadge } from '../../components/ui';
import { getLocalRole } from './components/requestsUiUtils';

/* ---------------------------
   Helpers (defensive)
---------------------------- */

// Inclusive day count
function getRequestedDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  return Math.floor((end.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0)) / 86400000) + 1;
}

// Normalize arbitrary API shapes into arrays
function arrayFrom(payload) {
  const candidates = [
    payload,
    payload?.data,
    payload?.data?.data, // some Laravel resources wrap twice
    payload?.results,
    payload?.items,
    payload?.rows,
    payload?.list,
  ];
  for (const c of candidates) if (Array.isArray(c)) return c;
  return [];
}

// Find total records across common shapes
function totalFrom(payload, fallbackLen = 0) {
  const candidates = [
    payload?.total,
    payload?.data?.total,
    payload?.meta?.total,
    payload?.pagination?.total,
  ];
  for (const t of candidates) if (typeof t === 'number' && !Number.isNaN(t)) return t;
  return fallbackLen;
}

// Map status -> MUI Chip color
function statusColor(status) {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'rejected') return 'error';
  if (s === 'cancelled' || s === 'canceled') return 'default';
  return 'default';
}

// Pending check (case-insensitive)
function isPending(status) {
  return (status || '').toLowerCase() === 'pending';
}

function getFinalHrStatus(req) {
  if (!req) return 'pending';

  if (String(req?.request_type || '').toLowerCase() === 'pre_approval') {
    const pre = String(req?.pre_approval_status || '').toLowerCase();
    if (pre === 'fully_approved') return 'approved';
    if (pre === 'rejected') return 'rejected';
    return 'pending';
  }

  const chain = [
    req.status,
    req.hr_status,
    req.finance_coordinator_status,
    req.finance_status,
    req.ceo_status,
  ].filter((v) => v != null && String(v).trim() !== '');

  if (!chain.length) return 'pending';

  const all = chain.map((v) => String(v).toUpperCase());
  if (all.includes('REJECTED')) return 'rejected';
  if (all.every((v) => v === 'APPROVED')) return 'approved';
  if (all.includes('PENDING')) return 'pending';

  const last = String(chain[chain.length - 1] || '').toLowerCase();
  return last || 'pending';
}

// Safely build attachment URL
function getAttachmentUrl(req) {
  if (!req) return null;
  return buildPublicFileUrl(req.attachment_url || req.attachment);
}

// --- Vacation approval workflow (for the details view) ---
const WF_VISUAL = {
  done: { color: 'success', label: 'Done' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  pending: { color: 'warning', label: 'Pending' },
  cancelled: { color: 'default', label: 'Cancelled' },
  upcoming: { color: 'default', label: 'Not started' },
};

function workflowStepIcon(state) {
  if (state === 'approved' || state === 'done') return <CheckCircleOutlineIcon fontSize="small" />;
  if (state === 'rejected') return <HighlightOffIcon fontSize="small" />;
  if (state === 'pending') return <PendingActionsIcon fontSize="small" />;
  if (state === 'cancelled') return <CancelIcon fontSize="small" />;
  return <RadioButtonUncheckedIcon fontSize="small" />;
}

// Manager step (status) -> HR step (hr_status). Returns ordered timeline steps.
function buildVacationWorkflow(req) {
  if (!req) return [];
  const norm = (s) => String(s || '').toLowerCase();
  const status = norm(req.status);
  const hrStatus = norm(req.hr_status);
  const cancelled = status === 'cancelled' || status === 'canceled';

  const managerState = cancelled ? 'cancelled'
    : status === 'approved' ? 'approved'
    : status === 'rejected' ? 'rejected'
    : 'pending';

  const hrState = cancelled ? 'cancelled'
    : hrStatus === 'approved' ? 'approved'
    : hrStatus === 'rejected' ? 'rejected'
    : status === 'approved' ? 'pending'
    : 'upcoming';

  return [
    {
      key: 'submitted', title: 'Request submitted', state: 'done',
      by: req?.user?.name || 'You', at: req?.created_at, note: null,
    },
    {
      key: 'manager', title: 'Manager review', state: managerState,
      by: req?.approver?.name || req?.approved_by_name || null,
      at: req?.approved_at,
      note: managerState === 'pending' ? 'Awaiting manager decision'
        : managerState === 'cancelled' ? 'Request was cancelled' : null,
    },
    {
      key: 'hr', title: 'HR review', state: hrState,
      by: req?.hr_approver?.name || null,
      at: req?.finalized_at,
      note: hrState === 'upcoming' ? 'Starts after manager approval'
        : hrState === 'pending' ? 'Awaiting HR decision'
        : hrState === 'cancelled' ? 'Request was cancelled' : null,
    },
  ];
}

function vacationOverallStatus(req) {
  const status = String(req?.status || '').toLowerCase();
  const hr = String(req?.hr_status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled') return 'cancelled';
  if (hr === 'approved') return 'approved';
  if (status === 'rejected' || hr === 'rejected') return 'rejected';
  return 'pending';
}

// Short caption describing where a request currently sits in the workflow.
function vacationStageCaption(req) {
  const status = String(req?.status || '').toLowerCase();
  const hr = String(req?.hr_status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled') return 'Cancelled';
  if (status === 'rejected') return `Rejected by manager${req?.approver?.name ? ' · ' + req.approver.name : ''}`;
  if (hr === 'rejected') return `Rejected by HR${req?.hr_approver?.name ? ' · ' + req.hr_approver.name : ''}`;
  if (hr === 'approved') return `Approved${req?.hr_approver?.name ? ' · ' + req.hr_approver.name : ''}`;
  if (status === 'approved') return 'Awaiting HR review';
  return 'Awaiting manager';
}

// Compact Manager -> HR progress indicator for list rows/cards.
function VacationWorkflowMini({ req }) {
  const steps = buildVacationWorkflow(req).filter((s) => s.key !== 'submitted');
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {steps.map((s, i) => {
        const v = WF_VISUAL[s.state] || WF_VISUAL.upcoming;
        const short = s.key === 'manager' ? 'Manager' : 'HR';
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <Box sx={{ width: 12, height: 2, bgcolor: 'divider', borderRadius: 1 }} />}
            <Tooltip arrow title={`${s.title}: ${v.label}${s.by ? ' · ' + s.by : ''}${s.at ? ' · ' + formatDisplayDate(s.at) : ''}`}>
              <Chip
                size="small"
                label={short}
                color={v.color === 'default' ? 'default' : v.color}
                variant={v.color === 'default' ? 'outlined' : 'filled'}
                sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
              />
            </Tooltip>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// Open an attachment, but show a friendly message if the file is missing (404)
// instead of dumping the user on a raw Apache error page.
async function openAttachment(url) {
  if (!url) return;
  try {
    const res = await fetch(url, { method: 'HEAD' });
    if (res.ok) {
      window.open(url, '_blank', 'noopener');
    } else {
      toast.error('This attachment is no longer available on the server.');
    }
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

function formatDisplayDate(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(dt);
}

function formatTypeLabel(value) {
  return String(value || 'Unspecified').replace(/_/g, ' ');
}

// CSV utilities
function toCsvValue(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename, rows, headers) {
  const head = headers.map(h => toCsvValue(h.label)).join(',');
  const body = rows
    .map(r =>
      headers
        .map(h => toCsvValue(typeof h.get === 'function' ? h.get(r) : r[h.key]))
        .join(',')
    )
    .join('\n');
  const csv = `${head}\n${body}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function MetricTrend({ color }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 74,
        height: 38,
        display: { xs: 'none', sm: 'block' },
        alignSelf: 'flex-end',
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 92 42" width="100%" height="100%" preserveAspectRatio="none">
        <path
          d="M2 32 C15 29, 18 23, 29 26 C39 29, 42 18, 53 21 C65 25, 70 11, 80 13 C86 14, 88 7, 90 5"
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M2 38 C15 35, 18 29, 29 32 C39 35, 42 24, 53 27 C65 31, 70 17, 80 19 C86 20, 88 13, 90 11 L90 42 L2 42 Z"
          fill="currentColor"
          opacity="0.1"
        />
      </svg>
    </Box>
  );
}

function VacationMetric({ label, value, hint, icon, color = 'primary.main' }) {
  const theme = useTheme();
  const [paletteKey, shadeKey] = String(color).split('.');
  const accent = theme.palette?.[paletteKey]?.[shadeKey] || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: 1.25,
        minHeight: 96,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: alpha('#9db2c8', 0.38),
        borderLeft: `4px solid ${alpha(accent, 0.85)}`,
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f8fbff', 0.94)} 100%)`,
        boxShadow: '0 14px 28px rgba(15, 23, 42, 0.07)',
        color: accent,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '14px',
            display: 'grid',
            placeItems: 'center',
            color: accent,
            background: `linear-gradient(145deg, ${alpha(accent, 0.18)}, ${alpha(accent, 0.07)})`,
            border: `1px solid ${alpha(accent, 0.16)}`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 900, whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ lineHeight: 1, fontWeight: 950, color: '#102653', mt: 0.2 }}>
            {value}
          </Typography>
          {hint ? (
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.35 }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
        <MetricTrend color={accent} />
      </Stack>
    </Box>
  );
}

function VacationRequestsTable({ rows, onView, onCancel }) {
  const theme = useTheme();

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: '14px',
        borderColor: alpha('#9db2c8', 0.34),
        backgroundColor: alpha('#ffffff', 0.98),
        boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
        overflow: 'auto',
        '& .MuiTableCell-root': {
          py: 1.1,
          borderBottomColor: alpha('#9db2c8', 0.24),
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          backgroundColor: '#f5f9fd',
          color: alpha('#0f2842', 0.74),
          fontWeight: 850,
          fontSize: theme.typography.pxToRem(12),
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
      }}
    >
      <Table stickyHeader size="small" aria-label="Vacation requests">
        <TableHead>
          <TableRow>
            <TableCell>Request</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Days</TableCell>
            <TableCell>Workflow</TableCell>
            <TableCell>Attachment</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((req) => {
            const days = getRequestedDays(req?.start_date, req?.end_date);
            const attachUrl = getAttachmentUrl(req);

            return (
              <TableRow
                key={req?.id ?? `${req?.leave_type}-${req?.start_date}-${req?.end_date}`}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.035) },
                }}
                onClick={() => onView(req)}
              >
                <TableCell sx={{ minWidth: 210 }}>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 850, color: '#102653' }}>
                      {req?.leave_type || 'Leave request'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{req?.id || '-'} · Requested {formatDisplayDate(req?.created_at)}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 190 }}>
                  <Typography variant="body2">
                    {formatDisplayDate(req?.start_date)} - {formatDisplayDate(req?.end_date)}
                  </Typography>
                </TableCell>
                <TableCell>{Number.isFinite(days) && days > 0 ? days : '-'}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <VacationWorkflowMini req={req} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                    {vacationStageCaption(req)}
                  </Typography>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {attachUrl ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={(e) => { e.stopPropagation(); openAttachment(attachUrl); }}
                    >
                      View file
                    </Link>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      None
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ minWidth: 180 }}>
                  <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => onView(req)}>
                      View
                    </Button>
                    {isPending(req?.status) ? (
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => onCancel(req?.id)}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function VacationRequestsCards({ rows, onView, onCancel }) {
  return (
    <Stack spacing={1}>
      {rows.map((req) => {
        const days = getRequestedDays(req?.start_date, req?.end_date);
        const attachUrl = getAttachmentUrl(req);

        return (
          <Paper
            key={req?.id ?? `${req?.leave_type}-${req?.start_date}-${req?.end_date}`}
            variant="outlined"
            onClick={() => onView(req)}
            sx={{
              p: 1.25,
              borderRadius: '12px',
              borderColor: alpha('#9db2c8', 0.34),
              cursor: 'pointer',
              background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f7fbff', 0.95)} 100%)`,
              boxShadow: '0 12px 26px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                    {req?.leave_type || 'Leave request'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{req?.id || '-'} · {formatDisplayDate(req?.created_at)}
                  </Typography>
                </Box>
                <StatusBadge status={req?.status || 'pending'} />
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" variant="outlined" label={`${formatDisplayDate(req?.start_date)} - ${formatDisplayDate(req?.end_date)}`} />
                <Chip size="small" variant="outlined" label={`${Number.isFinite(days) && days > 0 ? days : '-'} day(s)`} />
                <VacationWorkflowMini req={req} />
              </Stack>

              <Stack direction="row" spacing={0.8} justifyContent="space-between" alignItems="center" onClick={(e) => e.stopPropagation()}>
                {attachUrl ? (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    variant="body2"
                    onClick={(e) => { e.stopPropagation(); openAttachment(attachUrl); }}
                  >
                    View attachment
                  </Link>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No attachment
                  </Typography>
                )}
                <Stack direction="row" spacing={0.7}>
                  <Button size="small" variant="outlined" onClick={() => onView(req)}>
                    View
                  </Button>
                  {isPending(req?.status) ? (
                    <Button size="small" color="warning" variant="outlined" onClick={() => onCancel(req?.id)}>
                      Cancel
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

/* ---------------------------
   Component
---------------------------- */

function EmployeeAllRequestsPage() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  // Role: 'hr_admin' | 'finance_coordinator' | 'manager' | 'employee'
  const [role] = useState(() => {
    try {
      return getLocalRole();
    } catch {
      return 'employee';
    }
  });

  // Tabs: 0 => All, 1 => Vacation, 2 => Exit/Entry, 3 => Other
  const [activeTab, setActiveTab] = useState(1);

  // ------------------------
  // ALL Requests state
  // ------------------------
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState('');
  const [allVacRequests, setAllVacRequests] = useState([]);
  const [allTypeFilter, setAllTypeFilter] = useState('all'); // all | vacation | exit_entry | other
  const [allSearch, setAllSearch] = useState('');

  // ------------------------
  // VACATION Requests state
  // ------------------------
  const [vacLoading, setVacLoading] = useState(false);
  const [vacError, setVacError] = useState('');
  const [vacRequests, setVacRequests] = useState([]);
  const [startFilter, setStartFilter] = useState('');
  const [endFilter, setEndFilter] = useState('');

  // client quick filters/sort
  const [vacStatusFilter, setVacStatusFilter] = useState('all');
  const [vacTypeFilter, setVacTypeFilter] = useState('all');
  const [vacSearch, setVacSearch] = useState('');
  const dense = true;
  const [sortBy, setSortBy] = useState('start_date'); // 'start_date' | 'end_date' | 'status' | 'hr_status' | 'leave_type'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  // Pagination (server-side)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);

  // Cancel confirm
  const [openCancel, setOpenCancel] = useState(false);
  const [cancelId, setCancelId] = useState(null);

  // View modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // ------------------------
  // EXIT/ENTRY Requests state
  // ------------------------
  const [exitLoading, setExitLoading] = useState(false);
  const [exitError, setExitError] = useState('');
  const [exitRequests, setExitRequests] = useState([]);
  const [exitStatusFilter, setExitStatusFilter] = useState('all');
  const [exitDetailsOpen, setExitDetailsOpen] = useState(false);
  const [selectedExitRequest, setSelectedExitRequest] = useState(null);

  // ------------------------
  // OTHER (HR) Requests state
  // ------------------------
  const [otherLoading, setOtherLoading] = useState(false);
  const [otherError, setOtherError] = useState('');
  const [otherRequests, setOtherRequests] = useState([]);
  const [otherSearch, setOtherSearch] = useState('');
  const [otherTypeFilter, setOtherTypeFilter] = useState('all');

  // ======================
  // Tabs
  // ======================
  const handleTabChange = (_e, newValue) => setActiveTab(newValue);

  /* ======================
     ALL Requests
  ====================== */
  useEffect(() => {
    if (activeTab === 0) fetchAllRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchAllRequests = async () => {
    setAllLoading(true);
    setAllError('');

    try {
      const vacationUrl =
        role === 'finance_coordinator'
          ? '/finance_coordinator/vacation-requests'
          : '/employee/vacation-requests';

      const exitUrl =
        role === 'finance_coordinator'
          ? '/finance_coordinator/exit-entry/list'
          : '/employee/exit-entry/list';

      const [vacRes, exitRes, otherRes] = await Promise.all([
        api.get(vacationUrl, { params: { paginate: false } }),
        api.get(exitUrl),
        api.get('/hr-requests', { params: { view_mode: 'my_requests' } }),
      ]);

      const vacRows = arrayFrom(vacRes?.data);
      setAllVacRequests(Array.isArray(vacRows) ? vacRows : []);

      const exitRows = arrayFrom(exitRes?.data);
      setExitRequests(Array.isArray(exitRows) ? exitRows : []);

      const otherRows = Array.isArray(otherRes?.data) ? otherRes.data : arrayFrom(otherRes?.data);
      setOtherRequests(Array.isArray(otherRows) ? otherRows : []);
    } catch (err) {
      console.error('Failed to fetch all requests:', err);
      setAllVacRequests([]);
      setExitRequests([]);
      setOtherRequests([]);
      setAllError(
        err?.response?.data?.message || 'Could not load requests. Please try again later.'
      );
    } finally {
      setAllLoading(false);
    }
  };

  /* ======================
     VACATION Requests
  ====================== */
  useEffect(() => {
    if (activeTab === 1) fetchVacationRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, rowsPerPage]);

  const fetchVacationRequests = async (dateStart = startFilter, dateEnd = endFilter) => {
    setVacLoading(true);
    setVacError('');

    try {
      const url =
        role === 'finance_coordinator'
          ? '/finance_coordinator/vacation-requests'
          : '/employee/vacation-requests';

      const res = await api.get(url, {
        params: {
          page: page + 1, // 1-based if backend expects it
          per_page: rowsPerPage,
          start_date: dateStart || undefined,
          end_date: dateEnd || undefined,
        },
      });

      const rows = arrayFrom(res?.data);
      const total = totalFrom(res?.data, rows.length);

      setVacRequests(Array.isArray(rows) ? rows : []);
      setTotalRecords(typeof total === 'number' ? total : rows.length);
    } catch (err) {
      console.error('Failed to fetch vacation requests:', err);
      setVacRequests([]); // ensure an array
      setTotalRecords(0);
      setVacError(
        err?.response?.data?.message ||
          'Could not load your vacation requests. Please try again later.'
      );
    } finally {
      setVacLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchVacationRequests(startFilter, endFilter);
  };

  const handleClearFilters = () => {
    setStartFilter('');
    setEndFilter('');
    setVacStatusFilter('all');
    setVacTypeFilter('all');
    setVacSearch('');
    setSortBy('start_date');
    setSortDir('desc');
    setPage(0);
    fetchVacationRequests('', '');
  };

  const handleRefresh = () => {
    fetchVacationRequests();
  };

  // Quick stats (this page of data)
  const vacStats = useMemo(() => {
    const stats = { pending: 0, approved: 0, rejected: 0, cancelled: 0, total: vacRequests.length };
    for (const r of vacRequests) {
      const s = (r?.status || '').toLowerCase();
      if (s === 'pending') stats.pending++;
      else if (s === 'approved') stats.approved++;
      else if (s === 'rejected') stats.rejected++;
      else if (s === 'cancelled' || s === 'canceled') stats.cancelled++;
    }
    return stats;
  }, [vacRequests]);

  // Unique leave types (from current page)
  const leaveTypeOptions = useMemo(() => {
    const set = new Set();
    for (const r of vacRequests) if (r?.leave_type) set.add(r.leave_type);
    return ['all', ...Array.from(set)];
  }, [vacRequests]);

  const hasVacFilters =
    Boolean(startFilter) ||
    Boolean(endFilter) ||
    vacStatusFilter !== 'all' ||
    vacTypeFilter !== 'all' ||
    Boolean(String(vacSearch || '').trim()) ||
    sortBy !== 'start_date' ||
    sortDir !== 'desc';

  // client-side filtering/sorting on current page
  const vacRowsRendered = useMemo(() => {
    let rows = (vacRequests || []).slice();

    if (vacStatusFilter !== 'all') {
      rows = rows.filter((r) => (r?.status || '').toLowerCase() === vacStatusFilter);
    }
    if (vacTypeFilter !== 'all') {
      rows = rows.filter((r) => (r?.leave_type || '') === vacTypeFilter);
    }
    if (vacSearch.trim()) {
      const q = vacSearch.trim().toLowerCase();
      rows = rows.filter((r) => {
        const hay = `${r?.leave_type || ''} ${r?.status || ''} ${r?.hr_status || ''} ${r?.start_date || ''} ${r?.end_date || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const va = a?.[sortBy];
      const vb = b?.[sortBy];

      // date sort for start_date / end_date
      if (sortBy === 'start_date' || sortBy === 'end_date') {
        const da = new Date(va || 0).getTime();
        const db = new Date(vb || 0).getTime();
        return (da - db) * dir;
      }
      // string compare fallback
      const sa = (va || '').toString().toLowerCase();
      const sb = (vb || '').toString().toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });

    return rows;
  }, [vacRequests, vacStatusFilter, vacTypeFilter, vacSearch, sortBy, sortDir]);

  // Export (current page after local filters/sort)
  const exportVacCsv = () => {
    const headers = [
      { label: 'Leave Type', key: 'leave_type' },
      { label: 'Start', key: 'start_date' },
      { label: 'End', key: 'end_date' },
      { label: 'Days', get: (r) => getRequestedDays(r?.start_date, r?.end_date) },
      { label: 'Status', key: 'status' },
      { label: 'HR Status', key: 'hr_status' },
    ];
    downloadCsv('vacation_requests.csv', vacRowsRendered, headers);
  };

  // Cancel vacation request
  const handleClickCancel = (requestId) => {
    setCancelId(requestId);
    setOpenCancel(true);
  };

  const confirmCancelVacation = async () => {
    if (!cancelId) return;
    try {
      const prefix = role === 'finance_coordinator' ? '/finance_coordinator' : '/employee';
      await api.patch(`${prefix}/vacation-requests/${cancelId}/cancel`);
      toast.success('Request cancelled successfully');
      setOpenCancel(false);
      setCancelId(null);
      fetchVacationRequests();
      fetchAllRequests();
    } catch (err) {
      console.error('Failed to cancel vacation request:', err);
      setVacError(err?.response?.data?.message || 'Could not cancel the request. Please try again.');
    }
  };

  const closeCancel = () => {
    setOpenCancel(false);
    setCancelId(null);
  };

  const handleViewDetails = (req) => {
    setSelectedRequest(req || null);
    setOpenModal(!!req);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  const handleNewRequest = () => navigate('/requests/new');

  const handleChangeVacationPage = (_event, newPage) => setPage(newPage);

  const handleChangeVacationRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setRowsPerPage(Number.isNaN(next) ? 5 : next);
    setPage(0);
  };

  /* ======================
     EXIT/ENTRY Requests
  ====================== */
  useEffect(() => {
    if (activeTab === 2) fetchExitRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchExitRequests = async () => {
    setExitLoading(true);
    setExitError('');

    try {
      const url =
        role === 'finance_coordinator'
          ? '/finance_coordinator/exit-entry/list'
          : '/employee/exit-entry/list';

      const res = await api.get(url);
      const rows = arrayFrom(res?.data);
      setExitRequests(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Failed to fetch exit/entry requests:', err);
      setExitRequests([]); // ensure an array
      setExitError(err?.response?.data?.message || 'Could not load exit/entry requests. Please try again later.');
    } finally {
      setExitLoading(false);
    }
  };

  // Cancel exit/entry
  const handleCancelExitRequest = async (requestId) => {
    try {
      await cancelExitEntryRequest(requestId);
      toast.success('Request cancelled successfully!');
      fetchExitRequests();
      fetchAllRequests();
    } catch (error) {
      console.error('Error cancelling exit/entry request:', error);
      toast.error(error?.response?.data?.message || 'Failed to cancel request');
    }
  };

  // Update exit/entry status (for HR)
  const handleExitRequestStatusUpdate = async (requestId, newStatus) => {
    try {
      await updateRequestStatus(requestId, { status: newStatus });
      toast.success(`Request ${newStatus} successfully!`);
      fetchExitRequests();
      fetchAllRequests();
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error(error?.response?.data?.message || 'Failed to update request status');
    }
  };

  const handleViewExitDetails = (req) => {
    setSelectedExitRequest(req || null);
    setExitDetailsOpen(!!req);
  };

  const handleCloseExitDetails = () => {
    setExitDetailsOpen(false);
    setSelectedExitRequest(null);
  };

  // exit local filters
  const exitRowsRendered = useMemo(() => {
    let rows = (exitRequests || []).slice();
    if (exitStatusFilter !== 'all') {
      rows = rows.filter((r) => (r?.status || '').toLowerCase() === exitStatusFilter);
    }
    return rows;
  }, [exitRequests, exitStatusFilter]);

  /* ======================
     OTHER (HR) Requests
  ====================== */
  useEffect(() => {
    if (activeTab === 3) fetchOtherRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchOtherRequests = async () => {
    setOtherLoading(true);
    setOtherError('');

    try {
      const res = await api.get('/hr-requests', { params: { view_mode: 'my_requests' } });
      const rows = Array.isArray(res?.data) ? res.data : arrayFrom(res?.data);
      setOtherRequests(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Failed to fetch other requests:', err);
      setOtherRequests([]);
      setOtherError(err?.response?.data?.message || 'Could not load other requests. Please try again later.');
    } finally {
      setOtherLoading(false);
    }
  };

  const otherTypeOptions = useMemo(() => {
    const set = new Set();
    (otherRequests || []).forEach((r) => {
      if (r?.request_type) set.add(r.request_type);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [otherRequests]);

  const otherRowsRendered = useMemo(() => {
    let rows = (otherRequests || []).slice();

    if (otherTypeFilter !== 'all') {
      rows = rows.filter((r) => String(r?.request_type || '') === String(otherTypeFilter));
    }

    const q = String(otherSearch || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const hay = [
          r?.id,
          r?.request_type,
          r?.description,
          r?.notes,
          r?.request_date,
          r?.created_at,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }

    rows.sort((a, b) => {
      const da = new Date(a?.created_at || a?.request_date || 0).getTime();
      const db = new Date(b?.created_at || b?.request_date || 0).getTime();
      return db - da;
    });

    return rows;
  }, [otherRequests, otherTypeFilter, otherSearch]);

  const otherTypeSections = useMemo(() => {
    const groups = new Map();
    (otherRowsRendered || []).forEach((req) => {
      const typeKey = String(req?.request_type || 'unspecified');
      if (!groups.has(typeKey)) groups.set(typeKey, []);
      groups.get(typeKey).push(req);
    });

    return Array.from(groups.entries())
      .sort((a, b) => formatTypeLabel(a[0]).localeCompare(formatTypeLabel(b[0])))
      .map(([type, rows]) => {
        const stats = { total: rows.length, pending: 0, approved: 0, rejected: 0 };
        rows.forEach((r) => {
          const s = String(getFinalHrStatus(r) || 'pending').toLowerCase();
          if (s === 'approved') stats.approved += 1;
          else if (s === 'rejected') stats.rejected += 1;
          else stats.pending += 1;
        });
        return { type, label: formatTypeLabel(type), rows, stats };
      });
  }, [otherRowsRendered]);

  const allCounts = useMemo(() => {
    const vacation = (allVacRequests || []).length;
    const exitEntry = (exitRequests || []).length;
    const other = (otherRequests || []).length;
    return { vacation, exitEntry, other, total: vacation + exitEntry + other };
  }, [allVacRequests, exitRequests, otherRequests]);

  const vacSummaryCards = [
    {
      key: 'total',
      label: 'Total Requests',
      value: vacStats.total,
      icon: <CalendarMonthIcon fontSize="small" />,
      tone: 'primary',
    },
    {
      key: 'pending',
      label: 'Pending',
      value: vacStats.pending,
      icon: <PendingActionsIcon fontSize="small" />,
      tone: 'warning',
    },
    {
      key: 'approved',
      label: 'Approved',
      value: vacStats.approved,
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      tone: 'success',
    },
    {
      key: 'rejected',
      label: 'Rejected',
      value: vacStats.rejected,
      icon: <HighlightOffIcon fontSize="small" />,
      tone: 'error',
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      value: vacStats.cancelled,
      icon: <EventBusyIcon fontSize="small" />,
      tone: 'default',
    },
  ];

  const allRowsRendered = useMemo(() => {
    const rows = [];

    (allVacRequests || []).forEach((r) => {
      const start = r?.start_date || '-';
      const end = r?.end_date || '-';
      const days = getRequestedDays(r?.start_date, r?.end_date);
      rows.push({
        kind: 'vacation',
        id: r?.id,
        title: r?.leave_type || 'Vacation',
        when: `${start} -> ${end}${Number.isFinite(days) && days > 0 ? ` (${days}d)` : ''}`,
        status: String(r?.status || 'pending').toLowerCase(),
        dateSort: new Date(r?.created_at || r?.start_date || 0).getTime() || 0,
        raw: r,
      });
    });

    (exitRequests || []).forEach((r) => {
      const vf = r?.validity_from_date || '-';
      const vt = r?.validity_to_date || '-';
      rows.push({
        kind: 'exit_entry',
        id: r?.id,
        title: r?.visa_type || 'Exit/Entry',
        when: `${vf} -> ${vt}`,
        status: String(r?.status || 'pending').toLowerCase(),
        dateSort: new Date(r?.created_at || r?.validity_from_date || 0).getTime() || 0,
        raw: r,
      });
    });

    (otherRequests || []).forEach((r) => {
      const finalStatus = getFinalHrStatus(r);
      const dt = r?.request_date || r?.created_at || '-';
      rows.push({
        kind: 'other',
        id: r?.id,
        title: String(r?.request_type || 'Other').replace(/_/g, ' '),
        when: dt,
        status: String(finalStatus || 'pending').toLowerCase(),
        dateSort: new Date(r?.created_at || r?.request_date || 0).getTime() || 0,
        raw: r,
      });
    });

    let out = rows;

    if (allTypeFilter !== 'all') {
      out = out.filter((r) => r.kind === allTypeFilter);
    }

    const q = String(allSearch || '').trim().toLowerCase();
    if (q) {
      out = out.filter((r) => {
        const hay = `${r.kind} ${r.id ?? ''} ${r.title ?? ''} ${r.when ?? ''} ${r.status ?? ''}`.toLowerCase();
        return hay.includes(q);
      });
    }

    out.sort((a, b) => (b.dateSort || 0) - (a.dateSort || 0));
    return out;
  }, [allVacRequests, exitRequests, otherRequests, allTypeFilter, allSearch]);

  const tabTitle =
    activeTab === 0
      ? 'My Requests'
      : activeTab === 1
        ? 'My Vacation Requests'
        : activeTab === 2
          ? 'My Exit/Entry Requests'
          : 'All Request Types';

  const tabSubtitle =
    activeTab === 0
      ? 'All your requests in one place.'
      : activeTab === 1
        ? 'Track, filter, and export your vacation requests.'
        : activeTab === 2
          ? 'Track and manage your exit/re-entry requests.'
          : 'Browse all HR request types with status and details.';

  const refreshAction =
    activeTab === 0
      ? fetchAllRequests
      : activeTab === 1
        ? handleRefresh
        : activeTab === 2
          ? fetchExitRequests
          : fetchOtherRequests;

  const tabCounters = {
    all: allCounts.total || allRowsRendered.length,
    vacation: vacRequests.length || allCounts.vacation,
    exitEntry: exitRequests.length || allCounts.exitEntry,
    other: otherRequests.length || allCounts.other,
  };

  const activeRequestTotal =
    activeTab === 0
      ? tabCounters.all
      : activeTab === 1
        ? tabCounters.vacation
        : activeTab === 2
          ? tabCounters.exitEntry
          : tabCounters.other;

  const commandStats =
    activeTab === 1
      ? [
          { label: 'Total', value: activeRequestTotal, tone: 'primary' },
          { label: 'Pending', value: vacStats.pending, tone: 'warning' },
          { label: 'Approved', value: vacStats.approved, tone: 'success' },
        ]
      : [
          { label: 'Total', value: activeRequestTotal, tone: 'primary' },
          { label: 'Vacation', value: tabCounters.vacation, tone: 'success' },
          { label: 'Exit/Entry', value: tabCounters.exitEntry, tone: 'info' },
        ];

  const sharedSurfaceSx = {
    borderRadius: 2.5,
    border: `1px solid ${alpha('#103a5a', 0.14)}`,
    background:
      `linear-gradient(175deg, ${alpha('#ffffff', 0.94)} 0%, ${alpha('#f5fbff', 0.86)} 100%)`,
    boxShadow: `0 12px 30px ${alpha('#103a5a', 0.08)}`,
    animation: 'allRequestsRise 320ms ease both',
  };

  const tabLabel = (label, count) => (
    <Stack direction="row" spacing={0.85} alignItems="center">
      <Box component="span">{label}</Box>
      <Box
        component="span"
        sx={{
          minWidth: 22,
          px: 0.7,
          borderRadius: 99,
          fontSize: '0.72rem',
          lineHeight: 1.35,
          fontWeight: 700,
          textAlign: 'center',
          color: alpha('#0f2842', 0.9),
          backgroundColor: alpha('#2389ba', 0.16),
        }}
      >
        {count}
      </Box>
    </Stack>
  );

  return (
    <PageLayout backgroundAccent>
      <Box
        sx={{
          fontFamily: '"Manrope", "Segoe UI", "Helvetica Neue", sans-serif',
          '@keyframes allRequestsRise': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Card
          variant="outlined"
          sx={{
            mb: 2.25,
            p: { xs: 1.6, md: 2.2 },
            borderRadius: '16px',
            borderColor: alpha('#9db2c8', 0.34),
            background:
              `linear-gradient(135deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f6fbff', 0.96)} 48%, ${alpha('#edf7fd', 0.94)} 100%)`,
            boxShadow: '0 18px 46px rgba(15, 23, 42, 0.09)',
            position: 'relative',
            overflow: 'hidden',
            animation: 'allRequestsRise 240ms ease both',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.58,
              pointerEvents: 'none',
              display: { xs: 'none', md: 'block' },
            }}
          >
            <svg viewBox="0 0 1160 220" width="100%" height="100%" preserveAspectRatio="none">
              <path
                d="M28 122 C130 186, 236 154, 333 101 C420 54, 501 45, 594 88 C682 129, 733 171, 825 101 C911 35, 974 73, 1047 126 C1085 153, 1120 143, 1142 118"
                fill="none"
                stroke="#1aa4c8"
                strokeWidth="3"
                strokeDasharray="10 14"
                strokeLinecap="round"
              />
              <path
                d="M420 152 L613 50 L792 148 Z"
                fill="none"
                stroke="#d8e8f3"
                strokeWidth="1"
              />
              <circle cx="610" cy="88" r="8" fill="#0d75d9" opacity="0.82" />
              <circle cx="820" cy="104" r="8" fill="#22a35a" opacity="0.82" />
              <circle cx="975" cy="86" r="8" fill="#f0a329" opacity="0.82" />
            </svg>
          </Box>

          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(420px, 0.9fr)' },
              gap: { xs: 1.6, lg: 2.4 },
              alignItems: 'center',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.9} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label="Requests"
                  sx={{
                    fontWeight: 850,
                    color: '#0f6c91',
                    borderColor: alpha('#0f94bf', 0.28),
                    backgroundColor: alpha('#0f94bf', 0.1),
                  }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={activeTab === 1 ? 'Vacation workflow' : 'Employee workspace'}
                  sx={{
                    fontWeight: 850,
                    color: '#1f3d7c',
                    borderColor: alpha('#1f3d7c', 0.22),
                    backgroundColor: alpha('#1f3d7c', 0.08),
                  }}
                  variant="outlined"
                />
              </Stack>
              <Typography
                component="h1"
                sx={{
                  color: '#102653',
                  fontSize: { xs: '2rem', md: '2.55rem' },
                  lineHeight: 1.04,
                  fontWeight: 950,
                  letterSpacing: 0,
                  mb: 0.75,
                }}
              >
                {tabTitle}
              </Typography>
              <Typography sx={{ color: alpha('#102653', 0.72), fontWeight: 650, maxWidth: 620 }}>
                {tabSubtitle}
              </Typography>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: '14px',
                borderColor: alpha('#9db2c8', 0.36),
                backgroundColor: alpha('#ffffff', 0.92),
                boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={refreshAction}
                    sx={{
                      minHeight: 42,
                      borderColor: alpha('#1f3d7c', 0.28),
                      color: '#102653',
                      fontWeight: 850,
                    }}
                  >
                    Refresh
                  </Button>
                  {activeTab === 1 ? (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={exportVacCsv}
                      sx={{
                        minHeight: 42,
                        borderColor: alpha('#0f94bf', 0.3),
                        color: '#0f6c91',
                        fontWeight: 850,
                      }}
                    >
                      Export CSV
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNewRequest}
                    sx={{
                      minHeight: 42,
                      px: 2.2,
                      fontWeight: 900,
                      background: 'linear-gradient(135deg, #214a9a 0%, #0f9cc2 100%)',
                      boxShadow: '0 12px 24px rgba(15, 117, 217, 0.22)',
                    }}
                  >
                    New Request
                  </Button>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 0.85,
                  }}
                >
                  {commandStats.map((item) => {
                    const accent =
                      item.tone === 'success'
                        ? theme.palette.success.main
                        : item.tone === 'warning'
                          ? theme.palette.warning.main
                          : item.tone === 'info'
                            ? theme.palette.info.main
                            : theme.palette.primary.main;
                    return (
                      <Box
                        key={item.label}
                        sx={{
                          p: 1,
                          borderRadius: '12px',
                          border: `1px solid ${alpha(accent, 0.18)}`,
                          backgroundColor: alpha(accent, 0.065),
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 850 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: '#102653', fontWeight: 950, fontSize: '1.55rem', lineHeight: 1 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Card>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.75,
          mb: 2.25,
          p: 0.7,
          borderColor: alpha('#103a5a', 0.16),
          background:
            `linear-gradient(140deg, ${alpha('#eff8ff', 0.95)} 0%, ${alpha('#f4f9fc', 0.98)} 45%, ${alpha('#ffffff', 0.98)} 100%)`,
          boxShadow: `0 12px 26px ${alpha('#103a5a', 0.08)}`,
          animation: 'allRequestsRise 260ms ease both',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={mdUp ? 'fullWidth' : 'scrollable'}
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 52,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              minHeight: 48,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              color: alpha('#0f2842', 0.76),
              transition: 'all 180ms ease',
              '& .MuiSvgIcon-root': {
                fontSize: 20,
                color: alpha('#1f3d7c', 0.8),
              },
            },
            '& .MuiTab-root:hover': {
              backgroundColor: alpha('#2389ba', 0.08),
            },
            '& .MuiTab-root.Mui-selected': {
              background:
                `linear-gradient(135deg, ${alpha('#2389ba', 0.2)} 0%, ${alpha('#1f3d7c', 0.12)} 100%)`,
              color: '#0f2842',
              boxShadow: `inset 0 0 0 1px ${alpha('#2389ba', 0.24)}`,
              '& .MuiSvgIcon-root': {
                color: '#1f3d7c',
              },
            },
          }}
        >
          <Tab icon={<RequestPageIcon />} iconPosition="start" label={tabLabel('All', tabCounters.all)} />
          <Tab icon={<PersonOutlineIcon />} iconPosition="start" label={tabLabel('Vacation', tabCounters.vacation)} />
          <Tab icon={<FlightTakeoffIcon />} iconPosition="start" label={tabLabel('Exit/Entry', tabCounters.exitEntry)} />
          <Tab icon={<RequestPageIcon />} iconPosition="start" label={tabLabel('All Types', tabCounters.other)} />
        </Tabs>
      </Paper>

      {/* ====================== TAB #0: ALL ====================== */}
      {activeTab === 0 && (
        <>
          {allLoading ? <LoadingState rows={6} /> : null}

          <Card
            variant="outlined"
            sx={{
              ...sharedSurfaceSx,
              p: { xs: 1.5, md: 2 },
              mb: 2,
              background:
                `linear-gradient(170deg, ${alpha('#ffffff', 0.96)} 0%, ${alpha('#eef8ff', 0.9)} 100%)`,
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel id="all-type-label">
                  <FilterListIcon sx={{ mr: 0.8 }} /> Type
                </InputLabel>
                <Select
                  labelId="all-type-label"
                  label="Type"
                  value={allTypeFilter}
                  onChange={(e) => setAllTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="vacation">Vacation</MenuItem>
                  <MenuItem value="exit_entry">Exit/Entry</MenuItem>
                  <MenuItem value="other">Other Types</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Search"
                value={allSearch}
                onChange={(e) => setAllSearch(e.target.value)}
                placeholder="Type, status, date..."
                sx={{ flex: 1, minWidth: 220 }}
              />

              <Button
                variant="text"
                color="inherit"
                startIcon={<FilterAltOffIcon />}
                onClick={() => {
                  setAllTypeFilter('all');
                  setAllSearch('');
                }}
              >
                Clear
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.8 }}>
              <Chip size="small" variant="outlined" label={`Vacation: ${allCounts.vacation}`} />
              <Chip size="small" variant="outlined" label={`Exit/Entry: ${allCounts.exitEntry}`} />
              <Chip size="small" variant="outlined" label={`Other: ${allCounts.other}`} />
              <Chip size="small" color="primary" variant="outlined" label={`Total: ${allCounts.total}`} />
            </Stack>
          </Card>

          {allError && <Alert severity="error">{allError}</Alert>}

          {(allRowsRendered || []).length === 0 ? (
            allLoading ? null : (
              <EmptyState compact title="No requests" description="No records found." />
            )
          ) : (
            <Card variant="outlined" sx={{ ...sharedSurfaceSx, p: 0.6 }}>
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 520,
                  borderRadius: 2,
                  border: `1px solid ${alpha('#103a5a', 0.08)}`,
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: alpha('#1f3d7c', 0.07),
                    color: alpha('#0f2842', 0.92),
                    fontWeight: 700,
                  },
                }}
              >
                <Table stickyHeader size={dense ? 'small' : 'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Dates</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(allRowsRendered || []).map((row) => {
                      const typeLabel =
                        row.kind === 'vacation'
                          ? 'Vacation'
                          : row.kind === 'exit_entry'
                            ? 'Exit/Entry'
                            : 'Other';

                      const view = () => {
                        if (row.kind === 'vacation') return handleViewDetails(row.raw);
                        if (row.kind === 'exit_entry') return handleViewExitDetails(row.raw);
                        return navigate(`/hr-requests/${row?.raw?.id}`);
                      };

                      return (
                        <TableRow
                          key={`${row.kind}-${row.id}`}
                          hover
                          sx={{
                            transition: 'background-color 160ms ease',
                            '&:hover': { backgroundColor: alpha('#2389ba', 0.045) },
                          }}
                        >
                          <TableCell>{typeLabel}</TableCell>
                          <TableCell>{row.title || '-'}</TableCell>
                          <TableCell>{row.when || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={statusColor(row.status)}
                              label={String(row.status || 'pending').toUpperCase()}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={view}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </>
      )}

      {/* ====================== TAB #1: VACATION ====================== */}
      {activeTab === 1 && (
        <>
          <Stack spacing={1.35}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.35, md: 1.65 },
                borderRadius: '14px',
                borderColor: alpha('#9db2c8', 0.34),
                background:
                  `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f7fbff', 0.94)} 100%)`,
                boxShadow: '0 14px 34px rgba(15, 23, 42, 0.07)',
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ md: 'center' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.1, color: '#102653' }}>
                      Leave request snapshot
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#102653', 0.66), fontWeight: 600 }}>
                      Status summary for the vacation requests currently loaded in the workspace.
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(5, minmax(0, 1fr))',
                    },
                    gap: 0.9,
                  }}
                >
                  {vacSummaryCards.map((card) => {
                    const color =
                      card.tone === 'success'
                        ? 'success.main'
                        : card.tone === 'warning'
                          ? 'warning.main'
                          : card.tone === 'error'
                            ? 'error.main'
                            : card.tone === 'default'
                              ? 'text.secondary'
                              : 'primary.main';
                    return <VacationMetric key={card.key} label={card.label} value={card.value} icon={card.icon} color={color} />;
                  })}
                </Box>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.35, md: 1.65 },
                borderRadius: '14px',
                borderColor: alpha('#9db2c8', 0.34),
                backgroundColor: alpha('#ffffff', 0.98),
                boxShadow: '0 14px 30px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Stack spacing={1.15}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ color: '#102653', fontWeight: 950, lineHeight: 1.15 }}>
                      Filter requests
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#102653', 0.62), fontWeight: 650 }}>
                      Narrow by leave type, date range, status, and ordering.
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`Showing ${vacRowsRendered.length} of ${totalRecords || vacRequests.length}`}
                    sx={{
                      ml: { sm: 'auto' },
                      width: 'fit-content',
                      fontWeight: 850,
                      color: '#0f6c91',
                      backgroundColor: alpha('#0f94bf', 0.1),
                      borderColor: alpha('#0f94bf', 0.22),
                    }}
                    variant="outlined"
                  />
                </Stack>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ lg: 'center' }}>
                  <TextField
                    label="Search requests"
                    placeholder="Leave type, status, date..."
                    value={vacSearch}
                    onChange={(e) => setVacSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: { xs: '100%', lg: 260 } }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="vac-status-label">Status</InputLabel>
                    <Select
                      labelId="vac-status-label"
                      label="Status"
                      value={vacStatusFilter}
                      onChange={(e) => setVacStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="vac-type-label">Leave Type</InputLabel>
                    <Select
                      labelId="vac-type-label"
                      label="Leave Type"
                      value={vacTypeFilter}
                      onChange={(e) => setVacTypeFilter(e.target.value)}
                    >
                      {leaveTypeOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt === 'all' ? 'All types' : opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="vac-sort-by-label">Sort By</InputLabel>
                    <Select
                      labelId="vac-sort-by-label"
                      label="Sort By"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="start_date">Start Date</MenuItem>
                      <MenuItem value="end_date">End Date</MenuItem>
                      <MenuItem value="leave_type">Leave Type</MenuItem>
                      <MenuItem value="status">Status</MenuItem>
                      <MenuItem value="hr_status">HR Status</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="vac-sort-dir-label">Order</InputLabel>
                    <Select
                      labelId="vac-sort-dir-label"
                      label="Order"
                      value={sortDir}
                      onChange={(e) => setSortDir(e.target.value)}
                    >
                      <MenuItem value="desc">Newest</MenuItem>
                      <MenuItem value="asc">Oldest</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={1}
                  rowGap={1}
                  alignItems={{ lg: 'center' }}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startFilter}
                    onChange={(e) => setStartFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 170 }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={endFilter}
                    onChange={(e) => setEndFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 170 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: '1 1 220px', minWidth: 180 }}
                  >
                    Showing {vacRowsRendered.length} of {vacRequests.length} loaded request(s). Total records: {totalRecords || vacRequests.length}.
                  </Typography>
                  <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      startIcon={<FilterAltOffIcon />}
                      disabled={!hasVacFilters}
                      onClick={handleClearFilters}
                    >
                      Reset
                    </Button>
                    <Button size="small" variant="contained" onClick={handleApplyFilters}>
                      Apply Dates
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Stack>

          {vacError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {vacError}
            </Alert>
          )}

          <Box sx={{ mt: 1.35 }}>
            {vacLoading ? (
              <LoadingState rows={6} />
            ) : vacRowsRendered.length === 0 ? (
              <EmptyState
                compact
                title="No vacation requests found"
                description="Adjust your filters or create a new request."
              />
            ) : mdUp ? (
              <VacationRequestsTable rows={vacRowsRendered} onView={handleViewDetails} onCancel={handleClickCancel} />
            ) : (
              <VacationRequestsCards rows={vacRowsRendered} onView={handleViewDetails} onCancel={handleClickCancel} />
            )}
          </Box>

          <Card
            variant="outlined"
            sx={{
              mt: 1.35,
              p: 0.2,
              borderRadius: '14px',
              borderColor: theme.hr?.line?.soft || 'divider',
              boxShadow: 'none',
            }}
          >
            <Box sx={{ px: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangeVacationPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeVacationRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          </Card>

        </>
      )}

      {/* ====================== TAB #2: EXIT/ENTRY ====================== */}
      {activeTab === 2 && (
        <>
          {exitLoading ? <LoadingState rows={6} /> : null}

          <Card
            variant="outlined"
            sx={{
              ...sharedSurfaceSx,
              p: { xs: 1.5, md: 2 },
              mb: 2,
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id="exit-status-label"><FilterListIcon sx={{ mr: .8 }} /> Status</InputLabel>
                <Select
                  labelId="exit-status-label"
                  label="Status"
                  value={exitStatusFilter}
                  onChange={(e) => setExitStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="text"
                color="inherit"
                startIcon={<FilterAltOffIcon />}
                onClick={() => setExitStatusFilter('all')}
              >
                Clear
              </Button>
            </Stack>
          </Card>

          {exitError && <Alert severity="error">{exitError}</Alert>}

          {(exitRowsRendered || []).length === 0 ? (
            exitLoading ? null : (
              <EmptyState
                compact
                title="No exit/entry requests"
                description="No records found for this tab."
              />
            )
          ) : (
            <Card variant="outlined" sx={{ ...sharedSurfaceSx, p: 0.6 }}>
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 520,
                  borderRadius: 2,
                  border: `1px solid ${alpha('#103a5a', 0.08)}`,
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: alpha('#1f3d7c', 0.07),
                    color: alpha('#0f2842', 0.92),
                    fontWeight: 700,
                  },
                }}
              >
                <Table stickyHeader size={dense ? 'small' : 'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Validity From</TableCell>
                      <TableCell>Validity To</TableCell>
                      <TableCell>Visa Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(exitRowsRendered || []).map((req) => (
                      <TableRow
                        key={req?.id ?? `${req?.visa_type}-${req?.validity_from_date}-${req?.validity_to_date}`}
                        hover
                      >
                        <TableCell>{req?.validity_from_date ?? '-'}</TableCell>
                        <TableCell>{req?.validity_to_date ?? '-'}</TableCell>
                        <TableCell>{req?.visa_type ?? '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={statusColor(req?.status)}
                            label={req?.status || '-'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View details">
                            <IconButton size="small" sx={{ mr: 1 }} onClick={() => handleViewExitDetails(req)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {role === 'hr_admin' && isPending(req?.status) && (
                            <>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => handleExitRequestStatusUpdate(req?.id, 'approved')}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => handleExitRequestStatusUpdate(req?.id, 'rejected')}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {isPending(req?.status) && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleCancelExitRequest(req?.id)}
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

        </>
      )}

      {/* ====================== TAB #3: OTHER REQUESTS (HR) ====================== */}
      {activeTab === 3 && (
        <>
          {otherLoading ? <LoadingState rows={6} /> : null}

          <Card
            variant="outlined"
            sx={{
              ...sharedSurfaceSx,
              p: { xs: 1.5, md: 2 },
              mb: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Request Type Boards
                </Typography>
                <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' }, flexWrap: 'wrap' }}>
                  <Chip size="small" color="primary" variant="outlined" label={`Types: ${otherTypeSections.length}`} />
                  <Chip size="small" variant="outlined" label={`Requests: ${otherRowsRendered.length}`} />
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-end' }}>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel id="other-type-label">
                    <FilterListIcon sx={{ mr: 0.8 }} /> Focus Type
                  </InputLabel>
                  <Select
                    labelId="other-type-label"
                    label="Focus Type"
                    value={otherTypeFilter}
                    onChange={(e) => setOtherTypeFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {otherTypeOptions.map((t) => (
                      <MenuItem key={t} value={t}>
                        {formatTypeLabel(t)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Search"
                  value={otherSearch}
                  onChange={(e) => setOtherSearch(e.target.value)}
                  placeholder="Type, notes, date..."
                  sx={{ flex: 1, minWidth: 220 }}
                />

                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<FilterAltOffIcon />}
                  onClick={() => {
                    setOtherTypeFilter('all');
                    setOtherSearch('');
                  }}
                >
                  Clear
                </Button>
              </Stack>

              {otherTypeSections.length > 0 ? (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {otherTypeSections.map((section) => (
                    <Chip
                      key={section.type}
                      size="small"
                      clickable
                      variant={otherTypeFilter === section.type ? 'filled' : 'outlined'}
                      color={otherTypeFilter === section.type ? 'primary' : 'default'}
                      label={`${section.label} (${section.stats.total})`}
                      onClick={() => setOtherTypeFilter(section.type)}
                    />
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Card>

          {otherError && <Alert severity="error">{otherError}</Alert>}

          {(otherRowsRendered || []).length === 0 ? (
            otherLoading ? null : (
              <EmptyState
                compact
                title="No request types found"
                description="No records found for the selected type."
              />
            )
          ) : (
            <Stack spacing={1.25}>
              {otherTypeSections.map((section, index) => (
                <Accordion
                  key={section.type}
                  defaultExpanded={index === 0 || otherTypeFilter !== 'all'}
                  disableGutters
                  sx={{
                    border: `1px solid ${alpha('#103a5a', 0.15)}`,
                    borderRadius: '12px !important',
                    overflow: 'hidden',
                    boxShadow: `0 10px 24px ${alpha('#103a5a', 0.06)}`,
                    background:
                      `linear-gradient(170deg, ${alpha('#ffffff', 0.96)} 0%, ${alpha('#f4fbff', 0.88)} 100%)`,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      background:
                        `linear-gradient(115deg, ${alpha('#2389ba', 0.14)} 0%, ${alpha('#1f3d7c', 0.08)} 100%)`,
                    }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {section.label}
                      </Typography>
                      <Chip size="small" variant="outlined" label={`${section.stats.total} request(s)`} />
                      <Chip size="small" color="warning" variant="outlined" label={`Pending: ${section.stats.pending}`} />
                      <Chip size="small" color="success" variant="outlined" label={`Approved: ${section.stats.approved}`} />
                      <Chip size="small" color="error" variant="outlined" label={`Rejected: ${section.stats.rejected}`} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1.25 }}>
                    <Stack spacing={1}>
                      {section.rows.map((req) => {
                        const finalStatus = getFinalHrStatus(req);
                        const detailsValue = String(req?.notes || req?.description || '').trim();
                        const detailsShort =
                          detailsValue.length > 120 ? `${detailsValue.slice(0, 120)}...` : detailsValue;
                        return (
                          <Paper
                            key={req?.id}
                            variant="outlined"
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              borderColor: alpha('#103a5a', 0.12),
                              backgroundColor: alpha('#ffffff', 0.9),
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={{ md: 'center' }}
                            >
                              <Stack spacing={0.6} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  Request #{req?.id ?? '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Requested: {formatDisplayDate(req?.request_date || req?.created_at)}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {detailsShort || '-'}
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1} alignSelf={{ xs: 'flex-start', md: 'center' }}>
                                <Chip
                                  size="small"
                                  color={statusColor(finalStatus)}
                                  label={String(finalStatus || 'pending').toUpperCase()}
                                  variant="outlined"
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => navigate(`/hr-requests/${req?.id}`)}
                                >
                                  View
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Cancel Dialog */}
      <Dialog open={openCancel} onClose={closeCancel}>
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel this vacation request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancel}>No</Button>
          <Button color="warning" variant="contained" onClick={confirmCancelVacation}>
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vacation Details Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        {selectedRequest && (() => {
          const wf = buildVacationWorkflow(selectedRequest);
          const overall = vacationOverallStatus(selectedRequest);
          const days = getRequestedDays(selectedRequest?.start_date, selectedRequest?.end_date);
          const detailRows = [
            ['Country', selectedRequest?.country],
            ['Phone number', selectedRequest?.phone_number],
            ['Location in country', selectedRequest?.location_in_country],
          ].filter(([, val]) => val != null && String(val).trim() !== '');
          const attach = getAttachmentUrl(selectedRequest);
          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }}>
                    <FlightTakeoffIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                      {selectedRequest?.leave_type || 'Leave request'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Request #{selectedRequest?.id}{selectedRequest?.created_at ? ` · submitted ${formatDisplayDate(selectedRequest.created_at)}` : ''}
                    </Typography>
                  </Box>
                </Box>
                <StatusBadge status={overall} />
              </DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25, mb: 2 }}>
                  {[
                    ['From', formatDisplayDate(selectedRequest?.start_date)],
                    ['To', formatDisplayDate(selectedRequest?.end_date)],
                    ['Duration', `${days} day(s)`],
                  ].map(([l, val]) => (
                    <Box key={l} sx={{ bgcolor: 'action.hover', borderRadius: '10px', p: 1.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{l}</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{val}</Typography>
                    </Box>
                  ))}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <PendingActionsIcon fontSize="small" /> Approval workflow
                </Typography>
                <Box>
                  {wf.map((step, i) => {
                    const v = WF_VISUAL[step.state] || WF_VISUAL.upcoming;
                    const isLast = i === wf.length - 1;
                    const meta = [step.by, step.at ? formatDisplayDate(step.at) : null, step.note].filter(Boolean).join(' · ');
                    return (
                      <Box key={step.key} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: isLast ? 0 : 2 }}>
                        {!isLast && (<Box sx={{ position: 'absolute', left: 11, top: 26, bottom: 0, width: '2px', bgcolor: 'divider' }} />)}
                        <Box sx={{ zIndex: 1, lineHeight: 0, color: v.color === 'default' ? 'text.disabled' : `${v.color}.main` }}>
                          {workflowStepIcon(step.state)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, mt: '-2px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 700 }}>{step.title}</Typography>
                            <Chip
                              size="small"
                              label={v.label}
                              color={v.color === 'default' ? 'default' : v.color}
                              variant={v.color === 'default' ? 'outlined' : 'filled'}
                              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                            />
                          </Box>
                          {meta ? (<Typography variant="caption" color="text.secondary">{meta}</Typography>) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {(detailRows.length > 0 || selectedRequest?.description || attach) && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2.5, mb: 1 }}>Request details</Typography>
                    {detailRows.length > 0 && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 1 }}>
                        {detailRows.map(([l, val]) => (
                          <Box key={l}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{l}</Typography>
                            <Typography variant="body2">{val}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {selectedRequest?.description && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Description / reason</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRequest.description}</Typography>
                      </Box>
                    )}
                    {attach && (
                      <Box sx={{ mt: 1.5 }}>
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => openAttachment(attach)}>
                          View attachment
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </DialogContent>
            </>
          );
        })()}
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Exit/Entry Details Dialog */}
      <Dialog open={exitDetailsOpen} onClose={handleCloseExitDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Exit/Entry Request Details</DialogTitle>
        {selectedExitRequest && (
          <DialogContent dividers>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Validity From:</strong> {selectedExitRequest?.validity_from_date ?? '-'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Validity To:</strong> {selectedExitRequest?.validity_to_date ?? '-'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Visa Type:</strong> {selectedExitRequest?.visa_type ?? '-'}
            </Typography>
            {selectedExitRequest?.period_in_days != null && String(selectedExitRequest.period_in_days) !== '' && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Period (days):</strong> {selectedExitRequest.period_in_days}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {selectedExitRequest?.status ?? '-'}
            </Typography>
            {selectedExitRequest?.country && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Country:</strong> {selectedExitRequest.country}
              </Typography>
            )}
            {selectedExitRequest?.phone_number && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Phone Number:</strong> {selectedExitRequest.phone_number}
              </Typography>
            )}
            {selectedExitRequest?.location_in_country && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Location in Country:</strong> {selectedExitRequest.location_in_country}
              </Typography>
            )}
            {selectedExitRequest?.reason && (
              <Typography variant="body1" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                <strong>Reason:</strong> {selectedExitRequest.reason}
              </Typography>
            )}
            {selectedExitRequest?.document_path && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Document:</strong>{' '}
                <Link href={buildPublicFileUrl(selectedExitRequest.document_path)} target="_blank" rel="noopener">
                  View
                </Link>
              </Typography>
            )}
            {selectedExitRequest?.approval_attachment_path && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Approval Attachment:</strong>{' '}
                <Link
                  href={buildPublicFileUrl(selectedExitRequest.approval_attachment_path)}
                  target="_blank"
                  rel="noopener"
                >
                  View
                </Link>
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseExitDetails}>Close</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </PageLayout>
  );
}

export default EmployeeAllRequestsPage;
